import React, { useMemo, useRef, useState } from "react";
import { Badge, Button, Card, Icon, Input, Segmented, Skeleton, StatePanel } from "../../components/ui";
import { useStoreWriteGuard } from "../../helpers/storeScope";
import { BRIEF_TILES, BADGE_TONE, categoryOf, groupQueue, isStale, nextUp, typeMeta } from "../actionTypes";
import { celebrationLine, greeting, hours, int, londonHour, money, timeLondon } from "../format";
import { MSG, useAddTodo, useDashboardCalendar } from "../useDashboardCalendar";
import ActionQueue, { ActionButtons, TypeIcon } from "../components/ActionQueue";
import { BarList, Progress, Ring } from "../components/charts";
import { DashCard, ListRow, Stat } from "../components/parts";
import { cardState } from "../components/cardState";
import TargetButton from "../components/TargetButton";

// ── Start / Close of day ─────────────────────────────────────────────────────
function DayBrief({ firstName, queue, myDay, scheduleCount, onJump }) {
  const [mode, setMode] = useState(() => (londonHour() >= 17 ? "close" : "start"));
  const { items } = queue;
  // The day's work = open items that are not backlog (> 30 days old).
  const open = queue.open.filter((it) => !isStale(it));
  const backlog = queue.open.length - open.length;
  const now = groupQueue(open).now.length;
  const recap = myDay.data?.recap || {};
  const todayStats = myDay.data?.today || {};
  const doneToday = Math.max(Number(todayStats.actions_done_today) || 0, items.length - open.length);
  const total = doneToday + open.length;
  const donePct = total ? Math.round((doneToday / total) * 100) : 0;
  const [moving, setMoving] = useState(false);

  const toggle = (
    <Segmented
      size="sm"
      label="Brief mode"
      value={mode}
      onChange={setMode}
      options={[
        { value: "start", label: "Start of day" },
        { value: "close", label: "Close of day" },
      ]}
    />
  );

  if (mode === "start") {
    return (
      <Card as="section" padding="none" className="dash-card dash-brief" aria-label="Start of day">
        <div className="dash-brief__top">
          <div>
            <h2 className="dash-brief__title">{greeting()}{firstName ? `, ${firstName}` : ""}</h2>
            <p className="dash-brief__line">
              <b className="dash-num">{open.length}</b> action{open.length === 1 ? "" : "s"} today · <b className="dash-num">{now}</b> need you now · <b className="dash-num">{scheduleCount}</b> on the calendar
              {backlog ? <> · <b className="dash-num">{backlog}</b> in the backlog</> : null}
            </p>
          </div>
          {toggle}
        </div>
        <div className="dash-brief__counts">
          {BRIEF_TILES.map((t) => {
            const n = open.filter((it) => t.types.includes(categoryOf(it.type))).length;
            return (
              <button key={t.label} type="button" className="dash-bc" onClick={() => onJump(t.types[0])}>
                <span className={`dash-bc__n dash-num dash-text--${t.tint}`}>{n}</span>
                <span className="dash-bc__l">{t.label}</span>
              </button>
            );
          })}
        </div>
      </Card>
    );
  }

  const carry = async () => {
    setMoving(true);
    try {
      await queue.moveToTomorrow(open);
    } finally {
      setMoving(false);
    }
  };

  return (
    <Card as="section" padding="none" className="dash-card dash-brief" aria-label="Close of day">
      <div className="dash-brief__top">
        <div>
          <h2 className="dash-brief__title">Wrapping up the day</h2>
          <p className="dash-brief__line">
            <b className="dash-num">{doneToday}</b> done · <b className="dash-num">{open.length}</b> still open
          </p>
        </div>
        {toggle}
      </div>
      <div className="dash-recap">
        <Ring value={donePct} label={`${donePct}%`} sub="done" />
        <div className="dash-recap__body">
          <div className="dash-mini-stats">
            <div><b className="dash-num">{money(recap.landed_today_pence)}</b><span>Landed today</span></div>
            <div><b className="dash-num">{int(recap.quotes_sent_today)}</b><span>Quotes sent</span></div>
            <div><b className="dash-num">{int(recap.payment_links_sent_today)}</b><span>Links sent</span></div>
            <div><b className="dash-num">{int(recap.deals_won_today)}</b><span>Deals won</span></div>
          </div>
          <div className="dash-recap__actions">
            <Button variant="primary" size="md" loading={moving} disabled={!open.length} data-btn="dashboard-move-tomorrow" onClick={carry}>
              Move {open.length} to tomorrow
            </Button>
            <span className="dash-muted">
              Tomorrow starts with: {open.slice(0, 2).map((it) => String(it.title).split(" · ")[0]).join(", ") || "a clear desk"}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ── Next up ──────────────────────────────────────────────────────────────────
function NextUp({ queue, cta }) {
  const next = nextUp(queue.open);
  // Same Master Store write guard as the Orders page's "+ Sourcing Enquiry".
  const { guardProps: storeGuard } = useStoreWriteGuard();
  if (queue.query.data === undefined) return null;
  if (!next) {
    return (
      <Card as="section" padding="none" className="dash-card dash-next" aria-label="Next up">
        {/* F5: the shared empty state, with the one action a quiet day invites. */}
        <StatePanel
          state="empty"
          compact
          icon="Check"
          title="Nothing waiting on you"
          description="New actions appear here when clients, orders and stock need you."
          action={{ label: "New sourcing enquiry", to: "/request/sourcing", ...storeGuard }}
        />
      </Card>
    );
  }
  return (
    <Card as="section" padding="none" className="dash-card dash-next" aria-label="Next up">
      <TypeIcon type={next.type} size="lg" />
      <div className="dash-next__body">
        <div className="dash-next__eyebrow">Next up · {typeMeta(next.type).label}</div>
        <h3 className="dash-next__title">{next.title}</h3>
        {next.subtitle ? <div className="dash-next__sub">{next.subtitle}{next.amount_pence ? ` · ${money(next.amount_pence)}` : ""}</div> : null}
        <div className="dash-next__row">
          <ActionButtons item={next} cta={cta} busy={cta.busyKey === next.key} size="md" primary />
          <Button variant="secondary" size="md" data-btn="dashboard-next-done" onClick={() => queue.markDone(next)}>Mark done</Button>
          <Button variant="ghost" size="md" data-btn="dashboard-next-snooze" onClick={() => queue.snooze(next, 15)}>Snooze 15m</Button>
          <Badge tone={BADGE_TONE[next.tone] || "muted"} className="dash-next__when">{next.when?.label || "Today"}</Badge>
        </div>
      </div>
    </Card>
  );
}

// ── What's on today ──────────────────────────────────────────────────────────
// Collections + to-dos (API) merged with today's Google Calendar events,
// sorted by time; untimed items first. Each event opens its Google page.
const GCAL_DAY = "https://calendar.google.com/calendar/r/day";
const sortKey = (e) => (e.time && /^\d\d:\d\d$/.test(e.time) ? e.time : "");

function mergeSchedule(schedule = [], calendarEvents = []) {
  const own = schedule.map((e, i) => ({
    id: `s${i}`,
    time: timeLondon(e.at),
    title: e.title,
    sub: e.subtitle,
    href: e.href,
    kind: e.kind,
  }));
  const cal = calendarEvents.map((e) => {
    const timed = e.start && String(e.start).includes("T");
    return {
      id: `g${e.id}`,
      time: timed ? timeLondon(e.start) || e.time : "",
      title: e.title,
      sub: e.location || "Google Calendar",
      // The API sends no htmlLink yet: fall back to the Meet link, then Google
      // Calendar's own day view, so every event is clickable.
      href: e.htmlLink || e.hangoutLink || GCAL_DAY,
      kind: "calendar",
    };
  });
  return [...own, ...cal].sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
}

function AddTodo({ onDone }) {
  const add = useAddTodo();
  const [title, setTitle] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    const ok = await add.add(title.trim());
    if (ok) {
      setTitle("");
      onDone();
    }
  };
  return (
    <form className="dash-todo-form" onSubmit={submit} aria-label="Add a to-do for today">
      <Input size="md" fullWidth autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Call back, prepare a box, chase a supplier…" aria-label="To-do" />
      <Button type="submit" variant="primary" size="md" loading={add.busy} disabled={!title.trim()} data-btn="dashboard-todo-add">Add</Button>
      <Button type="button" variant="ghost" size="md" onClick={onDone}>Cancel</Button>
      {add.error ? <span className="dash-bad dash-muted">{add.error}</span> : null}
    </form>
  );
}

/**
 * Where the Google Calendar stands — ALWAYS shown (loading skeleton, Connect,
 * "Google Calendar · email · Disconnect", or what went wrong + Reconnect).
 */
function CalendarState({ calendar }) {
  const c = calendar;
  if (c.loading) {
    return (
      <div className="dash-gcal-line" data-testid="gcal-loading" aria-busy="true">
        <Skeleton shape="text" lines={1} />
      </div>
    );
  }
  if (!c.configured) {
    return (
      <p className="dash-gcal-line dash-muted" data-testid="gcal-not-configured">
        <Icon name="Calendar" size="sm" /> {MSG.notConfigured}
      </p>
    );
  }
  const box = (title, text, button) => (
    <div className="dash-gcal" data-testid="gcal-box">
      <span className="dash-ico dash-ico--sm dash-tint--order" aria-hidden="true"><Icon name="Calendar" size="sm" /></span>
      <div className="dash-gcal__text">
        <b>{title}</b>
        <span>{text}</span>
      </div>
      {button}
    </div>
  );
  const connectBtn = (label = "Connect Google Calendar", onClick = c.connect) => (
    <Button variant="primary" size="sm" data-btn="dashboard-calendar-connect" loading={c.connecting} onClick={onClick}>{label}</Button>
  );
  let body;
  if (!c.connected) {
    // Not connected — or the status call itself failed: offer Connect either way.
    body = box("See your meetings here", "Connect your Google Calendar to merge today's events with collections and to-dos. Only Google Calendar is supported for now — no Outlook yet.", connectBtn());
  } else {
    const who = (
      <div className="dash-gcal-line dash-muted" data-testid="gcal-connected">
        <Icon name="Calendar" size="sm" />
        <span>Google Calendar{c.email ? ` · ${c.email}` : ""}</span>
        <Button variant="ghost" size="sm" className="dash-gcal-line__btn" data-btn="dashboard-calendar-disconnect" loading={c.disconnecting} onClick={c.disconnect}>Disconnect</Button>
      </div>
    );
    const p = c.problem;
    body = p ? (
      <>
        {box(p.title, p.text, p.action === "reconnect" ? connectBtn("Reconnect", c.reconnectNow) : p.action === "connect" ? connectBtn() : null)}
        {who}
      </>
    ) : who;
  }
  return (
    <>
      {body}
      {c.connectError ? <p className="dash-muted dash-bad" role="alert">{c.connectError}</p> : null}
    </>
  );
}

function Schedule({ myDay, calendar, onOpen }) {
  const [adding, setAdding] = useState(false);
  const events = useMemo(() => mergeSchedule(myDay.data?.schedule || [], calendar.events || []), [myDay.data, calendar.events]);
  const nowHm = timeLondon(new Date().toISOString());
  const nextIdx = events.findIndex((e) => e.time && e.time >= nowHm);
  const state = cardState(myDay, { what: "today's schedule", lines: 2 });

  return (
    <DashCard
      title="What's on today"
      info="whatsOn"
      meta={events.length ? (nextIdx >= 0 ? `Next: ${events[nextIdx].time}` : "Day complete") : null}
      tools={
        <Button variant="ghost" size="sm" data-btn="dashboard-todo-open" leadingIcon={<Icon name="Plus" size="sm" />} onClick={() => setAdding(true)}>
          Add to-do
        </Button>
      }
    >
      {adding ? <AddTodo onDone={() => setAdding(false)} /> : null}
      {state ||
        (events.length ? (
          <div className="dash-sched">
            {events.map((e, i) => {
              const cls = `dash-ev dash-ev--${e.kind || "todo"}${e.time && e.time < nowHm && i !== nextIdx ? " is-past" : ""}${i === nextIdx ? " is-now" : ""}`;
              const body = (
                <>
                  <span className="dash-ev__tm dash-num">{e.time || (e.kind === "todo" ? "To-do" : "All day")}</span>
                  <span className="dash-ev__tt">{e.title}</span>
                  {e.sub ? <span className="dash-ev__ts">{e.sub}</span> : null}
                </>
              );
              return e.href ? (
                <button key={e.id} type="button" className={cls} onClick={() => onOpen(e.href)}>{body}</button>
              ) : (
                <div key={e.id} className={cls}>{body}</div>
              );
            })}
          </div>
        ) : (
          <p className="dash-empty">Nothing booked today.</p>
        ))}
      <CalendarState calendar={calendar} />
    </DashCard>
  );
}


// ── Goal ─────────────────────────────────────────────────────────────────────
function GoalCard({ myDay, me, repId, canSetTarget }) {
  const [period, setPeriod] = useState("week");
  const g = myDay.data?.goal?.[period] || {};
  const state = cardState(myDay, { what: "your goal", lines: 2 });
  const target = Number(g.target_pence) || 0;
  const landed = Number(g.landed_pence) || 0;
  const today = Number(g.today_pence) || 0;
  const p = target ? (landed / target) * 100 : null;
  const before = target ? ((landed - today) / target) * 100 : null;
  // Month target: the rep's own, or the STORE's when a manager views the whole
  // store. There is no weekly turnover target (the API always sends null).
  const repForTarget = repId || me?.default_rep_id || null;
  const targetKind = repForTarget ? "rep" : "store";
  const targetId = repForTarget || me?.store?.id;
  // canSetTarget = manager of the active store or RC master (DashboardPage):
  // the store target and any rep's. Anyone else may set only their own.
  const canSet = period === "month" && (canSetTarget || (targetKind === "rep" && repForTarget === me?.user?.id));

  return (
    <DashCard
      title="My goal"
      info="goal"
      tools={
        <Segmented size="sm" label="Goal period" value={period} onChange={setPeriod} options={[{ value: "week", label: "Week" }, { value: "month", label: "Month" }]} />
      }
    >
      {state || (
        <>
          <div className="dash-goal__num">
            <b className="dash-num">{money(landed)}</b>
            <span className="dash-num">{target ? `of ${money(target)}` : "landed"}</span>
          </div>
          {target ? <Progress value={before} ahead={p} label={`${Math.round(p)}% of target`} /> : null}
          <div className="dash-goal__foot">
            <span className="dash-num">
              {target ? <b>{Math.round(p)}%</b> : null}
              {target ? " · " : ""}+{money(today)} today
            </span>
            {!target ? (
              canSet && targetId ? (
                <TargetButton kind={targetKind} id={targetId} label={targetKind === "store" ? "Set store target" : "Set monthly target"} />
              ) : (
                <span>{period === "week" ? "No weekly target set" : "No monthly target set"}</span>
              )
            ) : null}
          </div>
        </>
      )}
    </DashCard>
  );
}

// ── Today stats ──────────────────────────────────────────────────────────────
function TodayCard({ myDay, queue, onJump }) {
  const t = myDay.data?.today || {};
  const state = cardState(myDay, { what: "today", lines: 2 });
  const open = queue.open.length;
  return (
    <DashCard title="Today" meta="since 00:00">
      {state || (
        <div className="dash-stats">
          <Stat label="Open actions" value={int(open || t.actions_open)} foot={`${queue.open.filter((it) => it.group === "now").length} need you now`} onClick={() => onJump("all")} />
          <Stat label="Done today" info="actionsDone" value={int(t.actions_done_today)} tone={t.actions_done_today ? "good" : undefined} />
          <Stat label="Zombie deals" info="zombie" value={int(t.zombie_deals)} tone={t.zombie_deals ? "warn" : "good"} foot="Idle 24h+" onClick={() => onJump("followup")} />
          <Stat label="Landed today" value={money(myDay.data?.recap?.landed_today_pence)} foot={`${int(myDay.data?.recap?.deals_won_today)} won`} />
        </div>
      )}
    </DashCard>
  );
}

// ── Celebrations ─────────────────────────────────────────────────────────────
function CelebrationsCard({ myDay, onOpen }) {
  const list = myDay.data?.celebrations || [];
  const state = cardState(myDay, { what: "celebrations", empty: !list.length, emptyText: "No birthdays or anniversaries in the next 7 days." });
  return (
    <DashCard title="Celebrations" info="celebrations" meta="next 7 days">
      {state || (
        <div className="dash-list">
          {list.slice(0, 6).map((c, i) => (
            <ListRow
              key={`${c.user_id}-${c.kind}-${i}`}
              lead={<TypeIcon type="client" size="sm" />}
              title={c.name}
              sub={celebrationLine(c)}
              action={
                <Button variant="secondary" size="sm" data-btn="dashboard-celebration-open" onClick={() => onOpen(`/users/client?userId=${c.user_id}`)}>
                  Prepare
                </Button>
              }
            />
          ))}
        </div>
      )}
    </DashCard>
  );
}

// ── My pipeline ──────────────────────────────────────────────────────────────
function PipelineCard({ myDay, title = "My pipeline" }) {
  const p = myDay.data?.pipeline || {};
  const opps = (p.opportunities || []).map((s) => ({ name: s.label || s.stage, value: s.count }));
  const leads = (p.leads || []).map((s) => ({ name: s.label || s.stage, value: s.count }));
  const sum = (rows) => rows.reduce((s, r) => s + (Number(r.value) || 0), 0);
  const state = cardState(myDay, { what: "your pipeline" });
  const now = p.lead_conversion_hours_avg;
  const prev = p.lead_conversion_hours_prev;
  const faster = now != null && prev != null ? prev - now : null;
  return (
    <DashCard title={title}>
      {state || (
        <div className="dash-stack">
          <div>
            <div className="dash-kpi-label">Opportunities by stage · <b className="dash-num">{int(sum(opps))}</b></div>
            {opps.length ? <BarList rows={opps} compact tone={1} /> : <p className="dash-empty">No open opportunities.</p>}
          </div>
          <div>
            <div className="dash-kpi-label">Leads by stage · <b className="dash-num">{int(sum(leads))}</b></div>
            {leads.length ? <BarList rows={leads} compact tone={2} /> : <p className="dash-empty">No open leads.</p>}
          </div>
          <div className="dash-stats dash-stats--one">
            <Stat
              label="Lead → opportunity time"
              info="leadConv"
              value={hours(now)}
              foot={faster == null ? "No previous period to compare" : faster >= 0 ? `${hours(faster)} faster than last period` : `${hours(-faster)} slower than last period`}
              tone={faster == null ? undefined : faster >= 0 ? "good" : "bad"}
            />
          </div>
        </div>
      )}
    </DashCard>
  );
}

export default function MyDayTab({ me, user, queue, cta, myDay, repId, canSetTarget }) {
  const calendar = useDashboardCalendar();
  const [filter, setFilter] = useState("all");
  const chipsRef = useRef(null);
  const jump = (type) => {
    setFilter(type);
    chipsRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  };
  const firstName = user?.first_name || String(me?.user?.name || "").split(" ")[0];
  const scheduleCount = (myDay.data?.schedule || []).length + (calendar.events || []).length;
  return (
    <div className="dash-grid dash-grid--main">
      <div className="dash-stack">
        <DayBrief firstName={firstName} queue={queue} myDay={myDay} scheduleCount={scheduleCount} onJump={jump} />
        <NextUp queue={queue} cta={cta} />
        <Schedule myDay={myDay} calendar={calendar} onOpen={cta.go} />
        <ActionQueue queue={queue} cta={cta} filter={filter} setFilter={setFilter} chipsRef={chipsRef} />
      </div>
      <div className="dash-stack">
        <GoalCard myDay={myDay} me={me} repId={repId} canSetTarget={canSetTarget} />
        <TodayCard myDay={myDay} queue={queue} onJump={jump} />
        <CelebrationsCard myDay={myDay} onOpen={cta.go} />
        <PipelineCard myDay={myDay} />
      </div>
    </div>
  );
}

